/**
 * Estado de gamificación derivado LOCAL-FIRST del ledger `xp_event` [L].
 *
 * La XP acumulada = suma del ledger (`sumXp`); el rango y el progreso salen del
 * reducer PURO `rankProgress` de `gamification-core`. Sin columna de sync nueva:
 * el ledger llega por el stream `my_gamification` y todo lo demás se recomputa en
 * el cliente. Respeta `prefers-reduced-motion` (las barras animan por CSS, que el
 * guard global desactiva).
 */
import { createMemo } from "solid-js";
import {
  allRanksStatus,
  getXpEventsQuery,
  queryKeys,
  rankPercentile,
  rankProgress,
  sumXp,
} from "@famidex/shared";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";

export function useGamification() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const enabled = () => Boolean(drizzle() && userId());

  const ledger = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.gamification.xpLedger(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getXpEventsQuery(d, uid) : null;
    },
  });

  const totalXp = createMemo(() => sumXp(ledger.data ?? []));
  const progress = createMemo(() => rankProgress(totalXp()));
  const ranks = createMemo(() => allRanksStatus(totalXp()));
  const percentile = createMemo(() => rankPercentile(totalXp()));

  return {
    userId,
    isPending: () => ledger.isPending,
    totalXp,
    progress,
    ranks,
    percentile,
  };
}
